import type { ReadinessResponse } from '@lousada/shared';

export type StatusTone =
  'online' | 'degraded' | 'offline' | 'unknown' | 'pending' | 'not-configured';

export type ReadinessOutcome =
  | { kind: 'loading' }
  | { kind: 'unreachable'; detail: string }
  | { kind: 'loaded'; readiness: ReadinessResponse };

export interface StatusRow {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly tone: StatusTone;
  readonly detail?: string;
}

const ROUTING_UNKNOWN_ROW: StatusRow = {
  id: 'routing',
  label: 'Planeamento de viagens',
  value: 'Desconhecido',
  tone: 'unknown',
  detail: 'Não pode ser verificado enquanto o serviço de dados estiver inacessível.',
};

function routingRow(readiness: ReadinessResponse): StatusRow {
  const { status, required } = readiness.routing;

  const presentation: Record<
    ReadinessResponse['routing']['status'],
    { value: string; tone: StatusTone }
  > = {
    not_configured: { value: 'Ainda não configurado', tone: 'not-configured' },
    up: { value: 'Operacional', tone: 'online' },
    degraded: { value: 'Degradado', tone: 'degraded' },
    down: { value: 'Indisponível', tone: 'offline' },
  };

  const { value, tone } = presentation[status];
  return {
    id: 'routing',
    label: 'Planeamento de viagens',
    value: required || status === 'up' ? value : `${value} (não exigido)`,
    tone,
    detail: ROUTING_DETAIL[status],
  };
}

const ROUTING_DETAIL: Record<ReadinessResponse['routing']['status'], string> = {
  not_configured: 'O planeamento de viagens não está ativo nesta instalação.',
  up: 'O motor de percursos responde e tem horários carregados.',
  degraded:
    'O motor de percursos responde, mas os horários carregados estão perto do fim ou expiraram.',
  down: 'O motor de percursos não está a responder.',
};

function webRow(): StatusRow {
  return { id: 'web', label: 'Aplicação', value: 'Operacional', tone: 'online' };
}

function loadingRow(id: string, label: string): StatusRow {
  return { id, label, value: 'A verificar…', tone: 'pending' };
}

function unknownRow(id: string, label: string, detail: string): StatusRow {
  return { id, label, value: 'Desconhecido', tone: 'unknown', detail };
}

function apiRow(): StatusRow {
  return {
    id: 'api',
    label: 'Serviço de dados',
    value: 'Operacional',
    tone: 'online',
  };
}

function postgresRow(readiness: ReadinessResponse): StatusRow {
  const { status, detail } = readiness.database;

  if (status === 'down') {
    return {
      id: 'postgres',
      label: 'Base de dados',
      value: 'Indisponível',
      tone: 'offline',
      ...(detail === undefined ? {} : { detail }),
    };
  }
  return {
    id: 'postgres',
    label: 'Base de dados',
    value: 'Operacional',
    tone: 'online',
  };
}

function postgisRow(readiness: ReadinessResponse): StatusRow {
  const { status, postgisVersion, detail } = readiness.database;

  if (status === 'down') {
    return unknownRow(
      'postgis',
      'Dados geográficos',
      'Não pode ser verificado enquanto a base de dados estiver inacessível.',
    );
  }
  if (postgisVersion === null) {
    return {
      id: 'postgis',
      label: 'Dados geográficos',
      value: 'Não instalado',
      tone: 'offline',
      ...(detail === undefined ? {} : { detail }),
    };
  }
  return {
    id: 'postgis',
    label: 'Dados geográficos',
    value: 'Operacional',
    tone: 'online',
  };
}

export function buildStatusRows(outcome: ReadinessOutcome): StatusRow[] {
  if (outcome.kind === 'loading') {
    return [
      webRow(),
      loadingRow('api', 'Serviço de dados'),
      loadingRow('postgres', 'Base de dados'),
      loadingRow('postgis', 'Dados geográficos'),
      loadingRow('routing', 'Planeamento de viagens'),
    ];
  }

  if (outcome.kind === 'unreachable') {
    return [
      webRow(),
      {
        id: 'api',
        label: 'Serviço de dados',
        value: 'Indisponível',
        tone: 'offline',
        detail: outcome.detail,
      },
      unknownRow(
        'postgres',
        'Base de dados',
        'Não pode ser verificado enquanto o serviço de dados estiver inacessível.',
      ),
      unknownRow(
        'postgis',
        'Dados geográficos',
        'Não pode ser verificado enquanto o serviço de dados estiver inacessível.',
      ),
      ROUTING_UNKNOWN_ROW,
    ];
  }

  return [
    webRow(),
    apiRow(),
    postgresRow(outcome.readiness),
    postgisRow(outcome.readiness),
    routingRow(outcome.readiness),
  ];
}

export function summarise(outcome: ReadinessOutcome): { text: string; tone: StatusTone } {
  switch (outcome.kind) {
    case 'loading':
      return { text: 'A verificar os serviços…', tone: 'pending' };
    case 'unreachable':
      return { text: 'Não foi possível contactar a API', tone: 'offline' };
    case 'loaded':
      switch (outcome.readiness.status) {
        case 'ok':
          return { text: 'Todos os serviços configurados estão operacionais', tone: 'online' };
        case 'degraded':
          return { text: 'Um dos serviços está degradado', tone: 'degraded' };
        case 'error':
          return { text: 'Um dos serviços está indisponível', tone: 'offline' };
      }
  }
}
